import React from 'react';
import { FileText, DollarSign, Calendar } from 'lucide-react';
import type { Invoice } from '@/hooks/useInvoices';

interface InvoiceCardProps {
  invoice: Invoice;
  onCardClick?: (dispatchId: string) => void;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, onCardClick }) => {
  const formatVehicleDescription = (
    year: number | null | undefined,
    make: string | null | undefined,
    model: string | null | undefined
  ) => {
    // If both make and model are not provided, show "Unspecified Vehicle"
    if (
      (!make || make === 'null' || make === 'Unknown') &&
      (!model || model === 'null' || model === 'Unknown')
    ) {
      return 'Unspecified Vehicle';
    }

    // Build the description parts
    const parts: string[] = [];

    // Add year only if it's valid (not 0, null, or blank)
    if (year && year > 0) {
      parts.push(year.toString());
    }

    // Add make if provided
    if (make && make !== 'null' && make !== 'unknown' && make !== 'Unknown') {
      parts.push(make);
    }

    // Add model if provided
    if (
      model &&
      model !== 'null' &&
      model !== 'unknown' &&
      model !== 'Unknown'
    ) {
      parts.push(model);
    }

    return parts.join(' ') || 'Unspecified Vehicle';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'SENT':
      case 'PENDING':
        return 'bg-yellow-100 text-amber-800';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    // If Square payment status is COMPLETED, show PAID
    if (invoice.squarePaymentStatus === 'COMPLETED') {
      return 'PAID';
    }
    // Use Square payment status if available, otherwise use invoice status
    if (invoice.squarePaymentStatus) {
      return invoice.squarePaymentStatus;
    }
    // If invoice status is COMPLETED, show PAID
    if (status === 'COMPLETED') {
      return 'PAID';
    }
    return status;
  };

  const vehicleInfo = invoice.dispatch?.vehicle
    ? formatVehicleDescription(
        invoice.dispatch.vehicle.vehicleYear,
        invoice.dispatch.vehicle.vehicleMake ??
          invoice.dispatch.vehicle.vehicleName,
        invoice.dispatch.vehicle.vehicleModel
      )
    : 'N/A';

  const dispatchNumber = invoice.dispatch?.dispatchNumber || 'N/A';

  const handleClick = () => {
    if (invoice.dispatchId && onCardClick) {
      onCardClick(invoice.dispatchId);
    }
  };

  return (
    <div
      className="bg-background border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleClick}
    >
      {/* Header with Invoice Info */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <FileText size={18} className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            Invoice #{invoice.invoiceNumber}
          </h3>
        </div>
        <span
          className={`text-xs px-2 py-1 rounded-full ${getStatusColor(
            getStatusText(invoice.status)
          )}`}
        >
          {getStatusText(invoice.status)}
        </span>
      </div>

      {/* Invoice Details Grid */}
      <div className="grid grid-cols-[35%_65%] gap-3 mb-3">
        <div className="flex items-center space-x-2">
          <DollarSign size={14} className="text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">Amount</p>
            <p className="text-sm font-medium text-gray-900">
              ${invoice.total.toFixed(2)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <FileText size={14} className="text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">Dispatch</p>
            <p className="text-sm font-medium text-gray-900">
              {dispatchNumber}
            </p>
          </div>
        </div>
      </div>

      {/* Customer and Vehicle Info */}
      <div className="grid grid-cols-[35%_65%] gap-3">
        <div className="flex items-center space-x-2">
          <FileText size={14} className="text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">Customer</p>
            <p className="text-sm font-medium text-gray-900">
              {invoice.clientName || 'N/A'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Calendar size={14} className="text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">Vehicle</p>
            <p className="text-sm font-medium text-gray-900">{vehicleInfo}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCard;
